import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ReduxStates } from '../../../redux/reduxTypes';
import { IdNameObject } from '../../../types';

import { ReactComponent as ExitIcon } from '../../../assets/icons/exit-icon.svg';

interface Props {
  options: IdNameObject[] | null;
  mappedValue?: string;
  mappingId: string;
  updateChanges: (option: string | null, mappingId: string) => void;
}

const MappingOptions = ({ options, mappedValue, mappingId, updateChanges }: Props): React.ReactElement => {
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const [inputValue, setInputValue] = useState<string>('');
  const [filteredOptions, setFilteredOptions] = useState<Array<IdNameObject> | null>(null);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const inputEl: any = useRef(null);

  const updateMappedValue = (option: string) => {
    if (option !== mappedValue) {
      updateChanges(option, mappingId);
    }
  };

  const handleOptionClick = (option: IdNameObject) => {
    updateMappedValue(option.name);
    setSelectedValue(option.name);
    setShowOptions(false);
  };

  const removeSelectedValue = () => {
    setSelectedValue(null);
    updateChanges(null, mappingId);
  };

  useEffect(() => {
    if (options) {
      if (inputValue.length > 0 && options) {
        const optionsCopy = [...options].filter((o) => o.name.toLowerCase().startsWith(inputValue.toLowerCase()));
        if (optionsCopy.length > 0) {
          setFilteredOptions(optionsCopy);
        } else {
          const newValueOption = [...options][0];
          setFilteredOptions([newValueOption]);
        }
      } else {
        setFilteredOptions([...options]);
      }
    }
  }, [inputValue]);

  useEffect(() => {
    if (options) setFilteredOptions([...options]);
  }, [options]);

  useEffect(() => {
    if (mappedValue) setSelectedValue(mappedValue);
  }, [mappedValue]);

  return (
    <div className="relative cursor-pointer flex items-center justify-between bg-white box-border rounded-[4px] border-[1px] rounded-b-[4px] px-[10px] min-h-[37px]">
      {selectedValue ? (
        <div className={`rounded py-[6.5px] px-2 text-sm font-nunitoBold leading-[21px] flex items-center w-max`}>
          {selectedValue}
          <ExitIcon
            className="h-2.5 cursor-pointer ml-[5px] fill-current text-navy"
            onClick={() => removeSelectedValue()}
          />
        </div>
      ) : (
        <input
          className="border-0 w-full text-sm font-nunitoRegular leading-[21px] focus:outline-none min-h-[35px] disabled:hidden"
          id={mappingId}
          ref={inputEl}
          onClick={() => setShowOptions(true)}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={copyData?.type_to_filter}
          autoComplete="off"
        />
      )}
      {showOptions && filteredOptions && (
        <div
          className={`absolute right-0 left-0 top-[40px] filter drop-shadow-dropdownFilter custom-scrollbar z-20 ${
            filteredOptions.length > 8 ? 'overflow-y-scroll h-[380px]' : ''
          }`}
        >
          <div className={`flex w-full border border-[#e5e5ea] bg-white rounded-[2px]`}>
            <div className="rounded-b-[1px] w-full p-[3px]">
              {filteredOptions?.map((option: any) => {
                return (
                  <div
                    key={option.id}
                    className={`m-0 font-nunitoRegular py-[13px] px-[10px] text-[14px] w-full border-none text-left hover:outline-none hover:bg-opaqueNavy hover:text-navy focus:outline-none focus:bg-opaqueNavy focus:text-navy last:border-0`}
                    onClick={() => handleOptionClick(option)}
                  >
                    {option.name}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {showOptions && (
        <button
          onClick={() => setShowOptions(false)}
          className={`fixed w-full h-full bg-transparent z-10 top-0 left-0`}
          style={{ outline: 0 }}
        />
      )}
    </div>
  );
};

export default MappingOptions;
